import { useEffect, useState, useMemo } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import Markdown from "react-markdown";
import { useHelpContent } from "./useHelpContent";
import { HelpcenterLink } from "./helpcenter-link.component";

interface HelpArticleViewProps {
	slug: string;
	onBack: () => void;
}

function mdxToMarkdown(raw: string): string {
	let text = raw;

	text = text.replace(/<CardGroup[^>]*>[\s\S]*?<\/CardGroup>/g, "");

	text = text.replace(/<Steps>([\s\S]*?)<\/Steps>/g, (_match, inner) => {
		let counter = 0;
		let result = inner as string;
		result = result.replace(/<Step title="([^"]*)">\s*/g, (_m: string, title: string) => {
			counter++;
			return `**${counter}. ${title}**\n\n`;
		});
		result = result.replace(/<Step title="([^"]*)"\/>/g, (_m: string, title: string) => {
			counter++;
			return `**${counter}. ${title}**\n`;
		});
		result = result.replace(/\s*<\/Step>/g, "\n");
		return result;
	});

	text = text.replace(/<Info>\s*/g, "> **ℹ️ Hinweis:** ");
	text = text.replace(/\s*<\/Info>/g, "\n");
	text = text.replace(/<Tip>\s*/g, "> **💡 Tipp:** ");
	text = text.replace(/\s*<\/Tip>/g, "\n");
	text = text.replace(/<Warning>\s*/g, "> **⚠️ Achtung:** ");
	text = text.replace(/\s*<\/Warning>/g, "\n");

	text = text.replace(/<Accordion title="([^"]*)">\s*/g, "**$1**\n\n");
	text = text.replace(/\s*<\/Accordion>/g, "\n");

	text = text.replace(/<[A-Z][^>]*\/>/g, "");
	text = text.replace(/<[A-Z][^>]*>|<\/[A-Z][A-Za-z]*>/g, "");

	text = text.replace(/\n{3,}/g, "\n\n");

	return text.trim();
}

export const HelpArticleView: React.FC<HelpArticleViewProps> = ({
	slug,
	onBack,
}) => {
	const { getArticle } = useHelpContent();
	const [article, setArticle] = useState<{
		title: string;
		markdown: string;
		url: string;
	} | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let stale = false;
		setLoading(true);
		getArticle(slug).then((data) => {
			if (stale) return;
			setArticle(data);
			setLoading(false);
		});
		return () => {
			stale = true;
		};
	}, [slug, getArticle]);

	const processedMarkdown = useMemo(
		() => (article ? mdxToMarkdown(article.markdown) : ""),
		[article]
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-8">
				<Loader2 className="h-5 w-5 animate-spin text-gray-400" />
			</div>
		);
	}

	if (!article) {
		return (
			<div className="text-center py-8">
				<p className="text-sm text-gray-500">Artikel nicht gefunden</p>
				<button
					onClick={onBack}
					className="mt-2 text-xs text-green-600 hover:underline"
				>
					Zurück
				</button>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<button
					onClick={onBack}
					className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
					aria-label="Zurück zur Artikelliste"
					data-testid="help-back-button"
				>
					<ArrowLeft className="h-3.5 w-3.5" />
					Zurück
				</button>
				<HelpcenterLink path={slug} />
			</div>

			<h2 className="text-base font-semibold text-gray-800">
				{article.title}
			</h2>

			<Markdown
				components={{
					h2: ({ children }) => (
						<h2 className="text-sm font-semibold text-gray-800 mt-5 mb-2">
							{children}
						</h2>
					),
					h3: ({ children }) => (
						<h3 className="text-[13px] font-semibold text-gray-700 mt-4 mb-1.5">
							{children}
						</h3>
					),
					h4: ({ children }) => (
						<h4 className="text-xs font-semibold text-gray-700 mt-3 mb-1">
							{children}
						</h4>
					),
					p: ({ children }) => (
						<p className="text-xs leading-relaxed text-gray-600 mb-2">
							{children}
						</p>
					),
					ul: ({ children }) => (
						<ul className="text-xs text-gray-600 space-y-1 pl-4 mb-2 list-disc">
							{children}
						</ul>
					),
					ol: ({ children }) => (
						<ol className="text-xs text-gray-600 space-y-1 pl-4 mb-2 list-decimal">
							{children}
						</ol>
					),
					li: ({ children }) => (
						<li className="leading-relaxed">{children}</li>
					),
					strong: ({ children }) => (
						<strong className="font-semibold text-gray-700">
							{children}
						</strong>
					),
					blockquote: ({ children }) => (
						<blockquote className="border-l-2 border-gray-200 pl-3 my-2 text-xs text-gray-600 [&>p]:mb-0">
							{children}
						</blockquote>
					),
					code: ({ children }) => (
						<code className="text-[11px] bg-gray-100 rounded px-1 py-0.5 font-mono text-gray-700">
							{children}
						</code>
					),
					table: ({ children }) => (
						<div className="overflow-x-auto my-2">
							<table className="text-xs w-full border-collapse">
								{children}
							</table>
						</div>
					),
					th: ({ children }) => (
						<th className="text-left text-xs font-semibold text-gray-700 border-b border-gray-200 pb-1.5 pr-3">
							{children}
						</th>
					),
					td: ({ children }) => (
						<td className="text-xs text-gray-600 border-b border-gray-100 py-1.5 pr-3">
							{children}
						</td>
					),
					a: ({ href, children }) => (
						<a
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-600 hover:underline"
						>
							{children}
						</a>
					),
					hr: () => <hr className="my-3 border-gray-100" />,
				}}
			>
				{processedMarkdown}
			</Markdown>
		</div>
	);
};
